/* eslint-disable */
import React, { createContext, useState } from "react";
import { Box } from "@mui/material";
// @ts-ignore
import { StriimButton } from "@striim/striim-ui";
// @ts-ignore
import { StriimMessageBox, StriimTypography } from "@striim/striim-ui-v2";
import TARGET_FORM_INFO from "src/modules/wizards/target/target-form-info";
import { validationUtils } from "src/modules/wizards/utils";
import VerticalStepper from "./source-configurator/vertical-stepper";
import { TargetConfigState } from "./target-configurator/target-configurator.types";
import TargetFormConfigStep from "./target-configurator/pages/target-form-config/target-form-config-step";
import ValidateTargetStep from "./target-configurator/pages/validate-target/validate-target-step";
import { buildCancelledResponse } from "../utils/hitl-cancel";

export interface TargetConfiguratorToolProps {
    args: { targetType: string };
    respond?: (result: string) => void;
}

const cardSx = {
    p: 2,
    border: "1px solid #e0e0e0",
    borderRadius: 2,
    mt: 1,
    backgroundColor: "background.paper",
    width: "100%"
};

interface TargetContextState extends TargetConfigState {
    setState: Function;
}

export const targetConfiguratorContext = createContext<TargetContextState>({
    targetType: "",
    formValues: {},
    setState: () => {}
});

export const TargetConfiguratorTool: React.FC<TargetConfiguratorToolProps> = ({ args, respond }) => {
    const targetType = args.targetType ?? "";
    const normalizedType = validationUtils.getTargetType(targetType).toUpperCase();

    if (!targetType || !(TARGET_FORM_INFO as any)[normalizedType]) {
        return (
            <Box sx={cardSx}>
                <Box mb={2}>
                    <StriimTypography variant="h2" sx={{ mb: 1, color: "primary.700" }}>
                        Configure Target
                    </StriimTypography>
                </Box>
                <Box mb={2}>
                    <StriimMessageBox
                        type="ERROR"
                        text={`Unknown target type: "${targetType}". Please provide a supported adapter (e.g. SnowflakeWriter, BigQuery, Postgres).`}
                        customCardStyles={{ width: "100%" }}
                    />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <StriimButton
                        variant="secondary"
                        size="small"
                        onClick={() => respond?.(JSON.stringify({ status: "unknown-target-type", targetType }))}
                    >
                        OK
                    </StriimButton>
                </Box>
            </Box>
        );
    }

    return <TargetConfiguratorStepper targetType={targetType} respond={respond} />;
};

const TargetConfiguratorStepper: React.FC<{
    targetType: string;
    respond?: (result: string) => void;
}> = ({ targetType, respond }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [done, setDone] = useState(false);
    const [state, setState] = useState<TargetConfigState>({ targetType, formValues: {} });

    const handleChange = (patch: Partial<TargetConfigState>) => setState(prev => ({ ...prev, ...patch }));
    const handleNext = () => setActiveStep(s => s + 1);
    const handleBack = () => setActiveStep(s => s - 1);
    const handleCancel = () => respond?.(buildCancelledResponse());

    const handleComplete = () => {
        setDone(true);
        respond?.(JSON.stringify({ status: "configured", targetType: state.targetType, formValues: state.formValues }));
    };

    const stepProps = { state, onChange: handleChange, onCancel: handleCancel };

    const steps = [
        {
            label: "Configure Target",
            content: <TargetFormConfigStep {...stepProps} onNext={handleNext} />
        },
        {
            label: "Validate Target",
            content: <ValidateTargetStep {...stepProps} onBack={handleBack} onNext={handleComplete} />
        }
    ];

    return (
        <targetConfiguratorContext.Provider value={{ ...state, setState }}>
            <Box sx={cardSx}>
                <Box sx={{ mb: 1 }}>
                    <StriimTypography variant="h2" sx={{ color: "primary.700" }}>
                        Configure {targetType} Target
                    </StriimTypography>
                </Box>

                {done ? (
                    <Box>
                        <StriimMessageBox
                            type="SUCCESS"
                            text="Target configured successfully."
                            customCardStyles={{ width: "100%" }}
                        />
                    </Box>
                ) : (
                    <VerticalStepper activeStep={activeStep} steps={steps} />
                )}
            </Box>
        </targetConfiguratorContext.Provider>
    );
};
